import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { M } from '@dashboard-experience/mastodon';
import { i18n } from '@international/mastodon-i18n';
import { debounce } from 'lodash';
import styled from 'styled-components';

import {
  suggestEmailDomain,
  isStaticTypoDomain,
  EmailSuggestion,
} from 'utils/emailDomainSuggestion';

const FormWrapper = styled.div`
  margin-top: 1rem;
`;

const ButtonRow = styled.div`
  margin-top: 1.5rem;
  display: flex;

  button {
    max-width: 100%;
    justify-content: center;
  }
`;

const SuggestionText = styled.div`
  margin-top: 0.25rem;
  font-size: 14px;
  color: var(--cds-text-secondary, #525252);
`;

const SuggestionLink = styled.button`
  background: none;
  border: none;
  padding: 0;
  font: inherit;
  color: var(--cds-link-primary, #0f62fe);
  cursor: pointer;
  text-decoration: underline;

  &:hover {
    color: var(--cds-link-primary-hover, #0043ce);
  }
`;

const PulseSuggestion = styled(SuggestionText)<{ pulse: boolean }>`
  @keyframes suggestion-pulse {
    0% {
      background-color: transparent;
    }
    30% {
      background-color: var(--cds-highlight, #d0e2ff);
    }
    100% {
      background-color: transparent;
    }
  }

  ${({ pulse }) =>
    pulse &&
    `
    animation: suggestion-pulse 0.8s ease-out;
    border-radius: 4px;
    padding: 2px 4px;
    margin-left: -4px;
  `}
`;

interface WorkOSEmailFormProps {
  onSubmit: (email: string) => void;
  onSuggestionShown?: (originalDomain: string, suggestedDomain: string) => void;
  onSuggestionAccepted?: (
    originalDomain: string,
    suggestedDomain: string,
  ) => void;
  onSuggestionDismissed?: (
    originalDomain: string,
    suggestedDomain: string,
  ) => void;
  processing: boolean;
}

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const WorkOSEmailForm: React.FC<WorkOSEmailFormProps> = ({
  onSubmit,
  onSuggestionShown,
  onSuggestionAccepted,
  onSuggestionDismissed,
  processing,
}) => {
  const [email, setEmail] = useState('');
  const [touched, setTouched] = useState(false);
  const [suggestion, setSuggestion] = useState<EmailSuggestion | null>(null);
  const [suggestionShownForEmail, setSuggestionShownForEmail] = useState('');
  const [suggestionDismissedOnce, setSuggestionDismissedOnce] = useState(false);
  const [pulseKey, setPulseKey] = useState(0);

  const isValidEmail = EMAIL_REGEX.test(email);
  const showEmailError = touched && email.length > 0 && !isValidEmail;

  const onSuggestionShownRef = useRef(onSuggestionShown);
  onSuggestionShownRef.current = onSuggestionShown;

  const suggestionShownForEmailRef = useRef(suggestionShownForEmail);
  suggestionShownForEmailRef.current = suggestionShownForEmail;

  const checkForSuggestion = useCallback((emailValue: string) => {
    const result = suggestEmailDomain(emailValue);
    if (result && result.suggested !== emailValue) {
      setSuggestion(result);
      if (emailValue !== suggestionShownForEmailRef.current) {
        setSuggestionShownForEmail(emailValue);
        const originalDomain = emailValue.split('@')[1] || '';
        const suggestedDomain = result.suggested.split('@')[1] || '';
        onSuggestionShownRef.current?.(originalDomain, suggestedDomain);
      }
    } else {
      setSuggestion(null);
    }
  }, []);

  const debouncedCheckForSuggestion = useMemo(
    () => debounce(checkForSuggestion, 300),
    [checkForSuggestion],
  );

  useEffect(
    () => () => debouncedCheckForSuggestion.cancel(),
    [debouncedCheckForSuggestion],
  );

  const handleBlur = () => {
    debouncedCheckForSuggestion.cancel();
    setTouched(true);
    if (isValidEmail) {
      checkForSuggestion(email);
    }
  };

  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;
    debouncedCheckForSuggestion.cancel();
    setEmail(value);
    setSuggestion(null);
    setSuggestionDismissedOnce(false);

    if (EMAIL_REGEX.test(value) && isStaticTypoDomain(value)) {
      debouncedCheckForSuggestion(value);
    }
  };

  const handleAcceptSuggestion = () => {
    if (!suggestion) return;
    const originalDomain = suggestion.original.split('@')[1] || '';
    const suggestedDomain = suggestion.suggested.split('@')[1] || '';
    setEmail(suggestion.suggested);
    setSuggestion(null);
    setSuggestionDismissedOnce(false);
    onSuggestionAccepted?.(originalDomain, suggestedDomain);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setTouched(true);
    if (!isValidEmail) return;

    // Check for typo on submit (covers mobile where blur may not fire,
    // and keystroke check may not have triggered yet). Skip if user
    // already saw and dismissed a suggestion for this exact email
    // (e.g., retrying after an API failure).
    if (!suggestion && email !== suggestionShownForEmail) {
      const result = suggestEmailDomain(email);
      if (result && result.suggested !== email) {
        setSuggestion(result);
        setSuggestionDismissedOnce(true);
        const originalDomain = email.split('@')[1] || '';
        const suggestedDomain = result.suggested.split('@')[1] || '';
        setSuggestionShownForEmail(email);
        onSuggestionShown?.(originalDomain, suggestedDomain);
        return;
      }
    }

    // First submit with active suggestion: block and let user review
    if (suggestion && !suggestionDismissedOnce) {
      setSuggestionDismissedOnce(true);
      setPulseKey(prev => prev + 1);
      return;
    }

    // Second submit (or no suggestion): dismiss and proceed
    if (suggestion) {
      const originalDomain = suggestion.original.split('@')[1] || '';
      const suggestedDomain = suggestion.suggested.split('@')[1] || '';
      onSuggestionDismissed?.(originalDomain, suggestedDomain);
      setSuggestion(null);
    }

    onSubmit(email);
  };

  return (
    <FormWrapper>
      <form onSubmit={handleSubmit} data-testid='workos-email-form'>
        <M.TextInput
          id='workos-email-input'
          data-testid='workos-email-input'
          type='email'
          labelText={i18n.getStr('components.WorkOSLogin.emailLabel')}
          placeholder={i18n.getStr('components.WorkOSLogin.emailPlaceholder')}
          value={email}
          onChange={handleEmailChange}
          onBlur={handleBlur}
          invalid={showEmailError}
          invalidText={i18n.getStr('components.WorkOSLogin.emailInvalid')}
          disabled={processing}
        />
        {suggestion && (
          <PulseSuggestion
            key={pulseKey}
            pulse={suggestionDismissedOnce}
            data-testid='workos-email-suggestion'
          >
            {i18n.getStr('components.WorkOSLogin.emailSuggestionPrefix')}
            <SuggestionLink
              type='button'
              data-testid='workos-email-suggestion-link'
              onClick={handleAcceptSuggestion}
            >
              {suggestion.suggested}
            </SuggestionLink>
            {i18n.getStr('components.WorkOSLogin.emailSuggestionSuffix')}
          </PulseSuggestion>
        )}
        <ButtonRow>
          <M.Button
            type='submit'
            kind='primary'
            data-testid='workos-continue-button'
            disabled={processing || !isValidEmail}
            style={{ width: '100%' }}
          >
            {processing && <M.LoadingInline className='button-loading-icon' />}
            {!processing &&
              suggestion &&
              suggestionDismissedOnce &&
              i18n.getStr('components.WorkOSLogin.continueWithEmailButton')}
            {!processing &&
              !(suggestion && suggestionDismissedOnce) &&
              i18n.getStr('components.WorkOSLogin.continueButton')}
          </M.Button>
        </ButtonRow>
      </form>
    </FormWrapper>
  );
};

export default i18n.renderTranslation()(WorkOSEmailForm);
